import { useState, useEffect, useMemo, useCallback } from "react"
import { FilterableSelect, type FilterableSelectOption } from "@/components/ui/filterable-select"
import {
  useErpDealNames,
  useErpAccAssignments,
  useErpAnalyticParts,
  type AnalyticPartDefinition,
  type AnalyticPartFieldDef,
  type AnalyticPartOption,
} from "../../hooks/useErpOptions"
import type { LineItemErpDraft } from "../../utils/invoiceFieldHelpers"

interface MergeDrawerErpFieldsProps {
  clientId: string
  isIncome?: boolean
  erpDraft: LineItemErpDraft
  onErpDraftChange: (draft: LineItemErpDraft) => void
}

export const MergeDrawerErpFields = ({
  clientId,
  isIncome = false,
  erpDraft,
  onErpDraftChange,
}: MergeDrawerErpFieldsProps): JSX.Element => {
  const { dealNames, isLoading: isLoadingDealNames } = useErpDealNames(clientId, isIncome)
  const { accAssignments, isLoading: isLoadingAccAssignments } = useErpAccAssignments(clientId, isIncome)
  const { definitions: analyticPartDefinitions, isLoading: isLoadingAnalyticParts } =
    useErpAnalyticParts(clientId, erpDraft.acc_assignment, isIncome)

  // Local selections for analytic part dropdowns (keyed by "accountCode-analyticPartId-rowNum")
  const [analyticSelections, setAnalyticSelections] = useState<Record<string, string>>({})

  const buildKey = (accountCode: string, analyticPartId: number, rowNum: number) =>
    `${accountCode}-${analyticPartId}-${rowNum}`

  // Get effective fields for a definition (backward compat if fields absent)
  const getEffectiveFields = (definition: AnalyticPartDefinition): AnalyticPartFieldDef[] => {
    if (definition.fields && definition.fields.length > 0) {
      return definition.fields
    }
    return [{
      field_id: 0,
      field_name: "",
      label: definition.account_name,
      row_num: 1,
      lookup_nomen_type_fk: definition.lookup_nomen_type_fk,
      options: definition.options,
      all_options: [],
    }]
  }

  // Sync selections from erpDraft.analytic_parts when definitions load
  useEffect(() => {
    if (analyticPartDefinitions.length === 0) {
      setAnalyticSelections({})
      return
    }

    const next: Record<string, string> = {}
    const parts = erpDraft.analytic_parts ?? []

    analyticPartDefinitions.forEach((def) => {
      const existing = parts.find((p) => p.acc === def.account_code)
      if (!existing) return

      const fields = getEffectiveFields(def)
      fields.forEach((field) => {
        const key = buildKey(def.account_code, def.analytic_part_id, field.row_num)
        const levelKey = `level${field.row_num}` as keyof typeof existing
        const levelNameKey = `level${field.row_num}_name` as keyof typeof existing
        const levelValue = existing[levelKey] as string | undefined
        const levelName = existing[levelNameKey] as string | undefined

        let resolved: string | undefined
        if (levelValue) {
          const match = field.options.find((o) => o.value === levelValue)
          if (match) resolved = match.value
        }
        if (!resolved && levelName) {
          const nameMatch = field.options.find(
            (o) => o.label.toLowerCase() === levelName.toLowerCase()
          )
          if (nameMatch) resolved = nameMatch.value
        }
        // Auto-select if single option on first field only
        if (!resolved && field.row_num === 1 && field.options.length === 1) {
          resolved = field.options[0].value
        }
        if (resolved) next[key] = resolved
      })
    })

    setAnalyticSelections(next)
  }, [analyticPartDefinitions, erpDraft.analytic_parts])

  // Build analytic parts payload from current selections — multilevel
  const buildAnalyticPayload = useCallback(
    (selections: Record<string, string>) => {
      return analyticPartDefinitions
        .map((def) => {
          const fields = getEffectiveFields(def)
          const entry: Record<string, string | undefined> = { acc: def.account_code }
          let hasAnyValue = false

          fields.forEach((field) => {
            const key = buildKey(def.account_code, def.analytic_part_id, field.row_num)
            const val = selections[key]
            const opt = field.options.find((o) => o.value === val)
            if (val) {
              entry[`level${field.row_num}`] = val
              entry[`level${field.row_num}_name`] = opt?.label?.trim() || val
              hasAnyValue = true
            }
          })

          return hasAnyValue ? entry : null
        })
        .filter(Boolean) as Array<{ acc: string; level1?: string; level1_name?: string; level2?: string; level2_name?: string; level3?: string; level3_name?: string }>
    },
    [analyticPartDefinitions]
  )

  const handleDealNameChange = useCallback(
    (value: string) => {
      const num = parseInt(value, 10)
      if (isNaN(num)) return
      onErpDraftChange({ ...erpDraft, deal_name: num })
    },
    [erpDraft, onErpDraftChange]
  )

  const handleAccAssignmentChange = useCallback(
    (value: string) => {
      const num = parseInt(value, 10)
      if (isNaN(num)) return
      setAnalyticSelections({})
      onErpDraftChange({ ...erpDraft, acc_assignment: num, analytic_parts: [] })
    },
    [erpDraft, onErpDraftChange]
  )

  const handleAnalyticChange = useCallback(
    (accountCode: string, analyticPartId: number, rowNum: number, value: string) => {
      const key = buildKey(accountCode, analyticPartId, rowNum)
      const updated = { ...analyticSelections, [key]: value }
      setAnalyticSelections(updated)
      onErpDraftChange({ ...erpDraft, analytic_parts: buildAnalyticPayload(updated) })
    },
    [erpDraft, onErpDraftChange, analyticSelections, buildAnalyticPayload]
  )

  // Options memos
  const dealNameOptions: FilterableSelectOption[] = useMemo(
    () =>
      dealNames.map((dn) => ({
        value: dn.id.toString(),
        label: dn.description,
        searchText: `${dn.vat_rate}% ${dn.id} ${dn.description}`,
      })),
    [dealNames]
  )

  const accAssignmentOptions: FilterableSelectOption[] = useMemo(
    () =>
      [...accAssignments]
        .sort((a, b) => (a.code || "").localeCompare(b.code || ""))
        .map((aa) => ({
          value: aa.id.toString(),
          label: aa.description,
          description: aa.code || undefined,
          searchText: `${aa.code || ""} ${aa.description}`,
        })),
    [accAssignments]
  )

  const getFieldOptions = (options: AnalyticPartOption[]): FilterableSelectOption[] =>
    [...options]
      .sort((a, b) => Number(a.value) - Number(b.value))
      .map((opt) => ({
        value: opt.value,
        label: opt.label,
        description: opt.apx_id != null ? `${opt.apx_id} · ${opt.value}` : opt.value,
        searchText: `${opt.apx_id ?? ""} ${opt.value} ${opt.label}`,
      }))

  return (
    <div className="space-y-3">
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
        ERP Assignment
      </h4>

      {/* Deal Name */}
      <div className="flex flex-col gap-1">
        <span className="text-xs text-gray-500 font-medium">Deal Name</span>
        <FilterableSelect
          value={erpDraft.deal_name?.toString() || ""}
          onValueChange={handleDealNameChange}
          disabled={isLoadingDealNames}
          loading={isLoadingDealNames}
          placeholder="Select deal name"
          searchPlaceholder="Search deals..."
          options={dealNameOptions}
          triggerClassName="h-9 text-sm border-gray-200 bg-white"
          renderSelectedValue={(opt) => {
            const dn = dealNames.find((d) => d.id.toString() === opt.value)
            return dn ? (
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1 py-0.5 rounded shrink-0">
                  {dn.vat_rate}%
                </span>
                <span className="truncate">{dn.description}</span>
              </div>
            ) : null
          }}
        />
      </div>

      {/* Account Assignment */}
      <div className="flex flex-col gap-1">
        <span className="text-xs text-gray-500 font-medium">Account Assignment</span>
        <FilterableSelect
          value={erpDraft.acc_assignment?.toString() || ""}
          onValueChange={handleAccAssignmentChange}
          disabled={isLoadingAccAssignments}
          loading={isLoadingAccAssignments}
          placeholder="Select account"
          searchPlaceholder="Search accounts..."
          options={accAssignmentOptions}
          triggerClassName="h-9 text-sm border-gray-200 bg-white"
          renderSelectedValue={(opt) => {
            const aa = accAssignments.find((a) => a.id.toString() === opt.value)
            return aa ? (
              <div className="flex items-center gap-1.5 min-w-0">
                {aa.code && (
                  <span className="text-[10px] bg-blue-100 text-blue-700 px-1 py-0.5 rounded shrink-0">
                    {aa.code}
                  </span>
                )}
                <span className="truncate">{aa.description}</span>
              </div>
            ) : null
          }}
        />
      </div>

      {/* Analytic Parts — grouped by account, multilevel fields */}
      {isLoadingAnalyticParts ? (
        <div className="text-sm text-gray-400 italic">Loading analytic parts...</div>
      ) : analyticPartDefinitions.length > 0 ? (
        analyticPartDefinitions.map((def) => {
          const fields = getEffectiveFields(def)
          const hasMultipleFields = fields.length > 1
          const groupKey = `${def.account_code}-${def.analytic_part_id}`

          return (
            <div key={groupKey} className="flex flex-col gap-1.5">
              {hasMultipleFields && (
                <span className="text-xs text-gray-500 font-medium">
                  {def.account_code} — {def.account_name}
                </span>
              )}
              {fields.map((field) => {
                const key = buildKey(def.account_code, def.analytic_part_id, field.row_num)
                return (
                  <div key={key} className={`flex flex-col gap-0.5 ${hasMultipleFields ? "pl-2" : ""}`}>
                    <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">
                      {hasMultipleFields ? field.label || `Level ${field.row_num}` : `${def.account_code} — ${def.account_name}`}
                    </span>
                    <FilterableSelect
                      value={analyticSelections[key] || ""}
                      onValueChange={(v) =>
                        handleAnalyticChange(def.account_code, def.analytic_part_id, field.row_num, v)
                      }
                      disabled={field.options.length === 0}
                      placeholder={`Select ${field.label || def.account_code}`}
                      searchPlaceholder={`Search ${field.label || "analytic parts"}...`}
                      options={getFieldOptions(field.options)}
                      triggerClassName="h-9 text-sm border-gray-200 bg-white"
                      renderSelectedValue={(opt) => (
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="text-[10px] bg-violet-100 text-violet-700 px-1 py-0.5 rounded shrink-0">
                            {opt.value}
                          </span>
                          <span className="truncate">{opt.label}</span>
                        </div>
                      )}
                    />
                  </div>
                )
              })}
            </div>
          )
        })
      ) : erpDraft.acc_assignment ? (
        <div className="text-sm text-gray-400 italic">No analytic parts for this account</div>
      ) : null}
    </div>
  )
}
